import React, { useState, useEffect } from 'react';
import api from '../api';
import { AlertTriangle, ShieldAlert, TrendingDown, UserX, Users as UsersIcon } from 'lucide-react';

const levelColor = (lvl) => lvl === 'RED' ? '#ef4444' : lvl === 'YELLOW' ? '#f59e0b' : '#10b981';
const levelBg = (lvl) => lvl === 'RED' ? 'rgba(239,68,68,0.12)' : lvl === 'YELLOW' ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.12)';

const Indicator = ({ icon: Icon, label, data }) => (
  <div className="glass" style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 16, borderLeft: `4px solid ${levelColor(data?.level)}` }}>
    <div style={{ background: levelBg(data?.level), padding: 12, borderRadius: 12 }}>
      <Icon size={24} color={levelColor(data?.level)} />
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700 }}>{data?.count ?? 0}</div>
    </div>
    <span style={{
      padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
      background: levelBg(data?.level), color: levelColor(data?.level),
    }}>{data?.level}</span>
  </div>
);

export const Risk = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetch(); }, []);

  const fetch = async () => {
    try {
      const res = await api.get('/admin/risk');
      setData(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loader-container"><div className="spinner"></div></div>;
  if (!data) return <div>No data</div>;

  const ind = data.indicators;

  return (
    <div>
      <h1 className="page-title">Risk Dashboard</h1>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 24 }}>
        <Indicator icon={TrendingDown} label="Missed Contributions" data={ind.missedContributions} />
        <Indicator icon={AlertTriangle} label="Failed Debits (30d)" data={ind.failedDebits} />
        <Indicator icon={UserX} label="High-Risk Users" data={ind.highRiskUsers} />
        <Indicator icon={ShieldAlert} label="Approaching Default" data={ind.membersApproachingDefault} />
        <Indicator icon={UsersIcon} label="Circles At Risk" data={ind.circlesAtRisk} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="glass" style={{ padding: 24 }}>
          <h3 style={{ marginTop: 0, marginBottom: 16 }}>Top Members with Missed Payments</h3>
          <div className="table-container" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Member</th><th>Circle</th><th>Missed</th><th>Owed</th></tr></thead>
              <tbody>
                {data.topMissedMembers.map(m => (
                  <tr key={m.memberId}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{m.userName}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{m.userEmail}</div>
                    </td>
                    <td>{m.circleName}</td>
                    <td><span className="badge rejected">{m.missedPayments}</span></td>
                    <td style={{ fontWeight: 600 }}>${m.amountOwed.toLocaleString()}</td>
                  </tr>
                ))}
                {data.topMissedMembers.length === 0 && (
                  <tr><td colSpan="4" style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>None — community is on track</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="glass" style={{ padding: 24 }}>
          <h3 style={{ marginTop: 0, marginBottom: 16 }}>Flagged / Low-Score Users</h3>
          <div className="table-container" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Name</th><th>Email</th><th>CiruScore</th><th>Flag</th></tr></thead>
              <tbody>
                {data.flaggedUsers.map(u => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 500 }}>{u.name}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{u.email}</td>
                    <td><span className="badge" style={{ background: u.ciruScore < 500 ? 'rgba(239,68,68,0.2)' : undefined, color: u.ciruScore < 500 ? '#fca5a5' : undefined }}>{u.ciruScore}</span></td>
                    <td>{u.flagged ? <span className="badge rejected">FLAGGED</span> : '—'}</td>
                  </tr>
                ))}
                {data.flaggedUsers.length === 0 && (
                  <tr><td colSpan="4" style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>No flagged users</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
